import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

import { EditSolidIcon } from '@dhl-ui/icons'
import { Body1, Body2, Box, Card, Grid, H6, IconButton } from '@dhl-ui/components'
import { DHL_EXPRESS_COUNTRIES as COUNTRIES } from '~/common/config/country'
import { PARCEL } from '../components/PromoCodeForm'
import { usePromoCode } from '~/modules/promocodes/hooks'
import type { LabelValue } from '~/common/model'
import { links } from '~/AppRoutes'

type Params = {
  id: string
}

export const PromoCodeDetails = () => {
  const { id } = useParams<Params>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { promoCode } = usePromoCode(id)

  const filter = (list: LabelValue[], inclusions: string[]) => list.filter((item) => inclusions.includes(item.value))

  const countryFrom = filter(COUNTRIES, promoCode?.fromCountries || [])
  const countriesFrom = countryFrom.map((item) => item.label).join(', ')
  const countryTo = filter(COUNTRIES, promoCode?.toCountries || [])
  const countriesTo = countryTo.map((item) => item.label).join(', ')
  const carriers = filter(PARCEL, promoCode?.carriers || [])
  const carriersList = carriers.map((item) => item.label).join(', ')

  const onClickEdit = () => id && navigate(links.editPromoCodeLink(id))

  return (
    <Card>
      <Card.Header
        title={t('admin.promoCodesPage.promoCodeForm.title')}
        endNode={
          <IconButton aria-label="edit promocode" onClick={onClickEdit}>
            <EditSolidIcon data-testid="edit-promocode" />
          </IconButton>
        }
      />
      <Card.Content>
        <Box>
          <Grid direction="row" container spacing={2}>
            <Grid size={{ sm: 12 }}>
              <Body2 variant="tertiary">{t('admin.promoCodesPage.promoCodeForm.code')}</Body2>
              <Body1>{promoCode?.key}</Body1>
            </Grid>
            <Grid size={{ sm: 12 }}>
              <H6>{t('admin.promoCodesPage.promoCodeForm.subtitle')}</H6>
            </Grid>
            <Grid size={{ sm: 6 }}>
              <Body2 variant="tertiary">{t('admin.promoCodesPage.promoCodeForm.discount')}</Body2>
              <Body1>{promoCode?.discountOnShippingCosts ? promoCode?.discountOnShippingCosts + ' %' : ''}</Body1>
            </Grid>
            <Grid size={{ sm: 6 }}>
              <Body2 variant="tertiary">{t('admin.promoCodesPage.promoCodeForm.maxUsages')}</Body2>
              <Body1>{promoCode?.maxUses}</Body1>
            </Grid>
            <Grid size={{ sm: 6 }}>
              <Body2 variant="tertiary">{t('admin.promoCodesPage.promoCodeForm.countryFrom')}</Body2>

              <Box>
                <Body1>{countriesFrom}</Body1>
              </Box>
            </Grid>

            <Grid size={{ sm: 6 }}>
              <Body2 variant="tertiary">{t('admin.promoCodesPage.promoCodeForm.countryTo')}</Body2>

              <Box>
                <Body1>{countriesTo}</Body1>
              </Box>
            </Grid>

            <Grid size={{ sm: 6 }}>
              <Body2 variant="tertiary">{t('admin.promoCodesPage.promoCodeForm.countryTo')}</Body2>

              <Box>
                <Body1>{countriesTo}</Body1>
              </Box>
            </Grid>
            <Grid size={{ sm: 12 }}>
              <Body2 variant="tertiary">{t('admin.promoCodesPage.promoCodeForm.carriers')}</Body2>
              <Box>
                <Body1>{carriersList}</Body1>
              </Box>
            </Grid>
            <Grid size={{ sm: 6 }}>
              <Body2 variant="tertiary">{t('admin.promoCodesPage.promoCodeForm.validFrom')}</Body2>
              <Body1>{dayjs(promoCode?.validFrom).format('DD MMMM YYYY')}</Body1>
            </Grid>
            <Grid size={{ sm: 6 }}>
              <Body2 variant="tertiary">{t('admin.promoCodesPage.promoCodeForm.validUntil')}</Body2>
              <Body1>{dayjs(promoCode?.validUntil).format('DD MMMM YYYY')}</Body1>
            </Grid>
          </Grid>
        </Box>
      </Card.Content>
    </Card>
  )
}
